






















































































import { mapGetters } from "vuex";
import Vue from "vue";
import { mixin as clickaway } from "vue-clickaway";
import iwInputCheckbox from "@/optimizedEncoding/components/buildingBlocks/InputCheckbox.vue";
import billboardImage from "@/assets/images/artworks/billboard.png";
import { resetPayment } from "@/shared/scripts/ValidateData";
import { billboardBlockedBy, removeBillboardAndNotify } from "@/optimizedEncoding/scripts/helpers/billboardEligibility";

export default Vue.extend({
    components: {
        iwInputCheckbox: iwInputCheckbox,
    },
    mixins: [clickaway],
    props: {
        label: {
            type: String,
            required: true
        },
        price: {
            type: String,
            default: ""
        },
        pathInStore: {
            type: String,
            required: true
        },
        optionType: {
            type: String,
            required: true
        },
        pack: {
            type: Object,
            required: false,
            default: null
        },
        activePack: {
            type: String,
            default: ""
        },
        includedInPack: {
            type: Boolean,
            required: false,
            default: false
        },
        // The pack this card represents is the one the customer is already published
        // on (upgrade flow). Its billboard option is informational only: not editable,
        // and reflects the known default (included-in-pack) rather than an in-progress
        // selection, since there's no signal for whether a paid billboard was purchased.
        isCurrentPack: {
            type: Boolean,
            required: false,
            default: false
        },
        // Renders the option purely as a hidden layout spacer: no store writes,
        // no events, no auto-selection (see PackPricing alignment spacers).
        isSpacer: {
            type: Boolean,
            required: false,
            default: false
        },
    },

    inject: {
        billboardPopoverState: { default: null }
    },
    data: function() {
        return {
            localShowBillboardPreview: false,
            billboardImage: billboardImage,
        };
    },

    computed: {
        ...mapGetters(
            {"getValueTransaction": "FormModule/getValueTransaction"}
        ),
        checkboxId: function(): string {
            const packId = this.pack?.title || "";
            return `option-checkbox-${packId}-${this.label}-${this.optionType}`;
        },
        billboardOptionId: function(): string {
            return this.pack ? `${this.pack.title}-${this.optionType}` : "";
        },
        billboardPopoverStateRef: function(): { activeId: string | null } | null {
            return (this as any).billboardPopoverState;
        },
        shouldHide: function(): boolean {
            return this.optionType === "billboard" && billboardBlockedBy() === "property";
        },
        isTaxBlocked: function(): boolean {
            return this.optionType === "billboard" && billboardBlockedBy() === "municipality";
        },
        isDisabled: function(): boolean {
            if (this.optionType !== "billboard") return false;
            return this.isCurrentPack || this.isTaxBlocked || billboardBlockedBy() === "billing";
        },
        showBillboardPreview: function(): boolean {
            const state = this.billboardPopoverStateRef;
            if (state) {
                return state.activeId === this.billboardOptionId;
            }
            return this.localShowBillboardPreview;
        },
        // Dynamically resolve the store path for this option (billboard or extension)
        packPath: function(): string {
            if (!this.pack) return "";

            const packIdentifier = (this.pack?.packType || this.pack?.title || "").toLowerCase();
            if (!packIdentifier) return "";

            if (this.optionType === "billboard") {
                return `optimizedEncoding.selectedPackOptions.${packIdentifier}.billboard`;
            }

            if (this.optionType === "extension") {
                return `optimizedEncoding.selectedPackOptions.${packIdentifier}.extension`;
            }

            return "";
        },

        selected: {
            get: function(): boolean {
                if (this.isTaxBlocked) {
                    return false;
                }
                if (this.optionType === "billboard" && this.isCurrentPack) {
                    return this.includedInPack;
                }
                const packKey = this.pack.title;
                const options =
                    this.$store.getters["FormModule/getValueTransaction"](
                        "optimizedEncoding.selectedPackOptions"
                    ) || {};
                return !!options[packKey]?.[this.optionType];
            },
            set: function(value: boolean) {
                if (this.isSpacer) return;
                if (!this.pack) return;
                if (value && this.optionType === "billboard" && (this.shouldHide || this.isDisabled)) {
                    return;
                }

                const packKey = this.pack.title;
                const allOptions =
                    this.$store.getters["FormModule/getValueTransaction"](
                        "optimizedEncoding.selectedPackOptions"
                    ) || {};

                // Build new per-pack options object
                const nextOptions = {
                    ...allOptions,
                    [packKey]: {
                        ...(allOptions[packKey] || {}),
                        [this.optionType]: value
                            ? this.optionType === "extension"
                                ? this.$store.getters["FormModule/getValueTransaction"]("transaction.pricing.extensionOptionDetails")
                                : { price: Number(this.price || 0) }
                            : null
                    }
                };

                // Save per-pack options
                this.$store.commit("FormModule/addValueTransaction", {
                    path: "optimizedEncoding.selectedPackOptions",
                    value: nextOptions
                });

                let extensionPrice = 0;

                if (this.optionType === "extension") {
                    const ext = value ? nextOptions[packKey].extension : null;

                    this.$store.commit("FormModule/addValueTransaction", {
                        path: "transaction.pricing.extensionOption",
                        value: ext
                    });

                    this.$store.commit("OrdersModule/setExtensionOption", ext ? {
                        packType: packKey.toLowerCase(),
                        ...ext
                    } : null);

                    extensionPrice = ext?.price || 0;
                }

                if (this.optionType === "billboard") {
                    const billboard = value ? nextOptions[packKey].billboard : null;

                    this.$store.commit("FormModule/addValueTransaction", {
                        path: "transaction.pricing.billboard",
                        value: billboard
                    });
                }

                const basePackPrice = Number(
                    this.$store.getters["FormModule/getValueTransaction"](
                        `tariff.visualizationOptions.${this.activePack}.price`
                    ) || 0
                );

                const billboardPrice = nextOptions[packKey].billboard?.price || 0;

                const totalPrice = basePackPrice + extensionPrice + billboardPrice;

                this.$store.commit("FormModule/addValueTransaction", {
                    path: "transaction.pricing.totalPrice",
                    value: totalPrice
                });
                resetPayment();
                this.$emit("update-classified");
            }
        },

        packExtension: function(): any {
            return this.$store.getters["FormModule/getValueTransaction"]("transaction.pricing.extensionOption");
        },

        packBillboard: function(): any {
            return this.$store.getters["FormModule/getValueTransaction"]("transaction.pricing.billboard");
        },

        formattedPrice: function(): string {
            if (!this.price) return "";

            if (this.includedInPack) {
                return `+ ${this.$t("included_in_pack") as string}`;
            }

            return `+ ${this.formatCurrency(this.price)}`;
        },

        // Resolve the option label at render time so it follows the active UI locale.
        // Billboard/discount labels are plain i18n keys; the extension label also needs
        // the (locale-dependent) duration unit, read from the same store value packConfig
        // used when building the option. IO-223
        displayLabel: function(): string {
            if (this.optionType === "extension") {
                const details = this.$store.getters["FormModule/getValueTransaction"](
                    "transaction.pricing.extensionOptionDetails"
                );
                const duration = details?.duration;
                if (duration) {
                    return this.$t(this.label, {
                        unit: this.pluralization(`x_${String(duration.unit).toLowerCase()}s`, duration.count)
                    }) as string;
                }
            }
            return this.$t(this.label) as string;
        },
    },

    watch: {
        // Drop any billboard already selected (edit funnel, or postal code changed to a taxed one).
        isTaxBlocked: {
            immediate: true,
            handler: function(blocked: boolean) {
                if (blocked && !this.isSpacer) {
                    removeBillboardAndNotify();
                }
            }
        },
        activePack: function(newVal: string) {
            if (this.isSpacer) return;
            if (newVal?.toLowerCase() === this.pack?.packType?.toLowerCase() && this.selected) {
                this.$emit("update-classified");
            }
        }
    },
    mounted: function() {
        if (this.isSpacer) {
            return;
        }
        if (this.shouldHide || this.isDisabled) {
            return;
        }
        if (this.pack?.title === "premium" && this.optionType === "billboard" && this.includedInPack) {
            // Default the included sign on only when the user has never chosen it
            // (stored value undefined). An explicit uncheck stores null - respect it
            // so the opt-out survives leaving and returning to this step (which
            // remounts the component and re-runs mounted()).
            const storedBillboard = (this.$store.getters["FormModule/getValueTransaction"](
                "optimizedEncoding.selectedPackOptions"
            ) || {})[this.pack.title]?.[this.optionType];
            if (storedBillboard === undefined) {
                this.selected = true;
            }
        }
        if (this.selected && this.pack?.packType.toLowerCase() === this.activePack.toLowerCase()) {
            this.$nextTick(() => {
                this.$emit("update-classified");
            });
        }
    },

    methods: {
        toggleBillboardPopover: function(): void {
            const state = this.billboardPopoverStateRef;
            if (state) {
                if (state.activeId === this.billboardOptionId) {
                    state.activeId = null;
                } else {
                    state.activeId = this.billboardOptionId;
                }
            } else {
                this.localShowBillboardPreview = !this.localShowBillboardPreview;
            }
        },
        handleBillboardClickAway: function(): void {
            const state = this.billboardPopoverStateRef;
            if (state) {
                state.activeId = null;
            } else {
                this.localShowBillboardPreview = false;
            }
        },
    }
});
